import { ScrollArea } from '@/components/ui/ScrollArea';
import { Card, CardContent, CardHeader, SearchInput } from '@/components/wm';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';

const MAX_LIMIT = 500;

export function DualListBox({
    error = null,
    height = '400px',
    onChange,
    onSearchEnter,
    options = [],
    searchPlaceholder = 'Search...',
    selectedIds = [],
    title = 'Select Items'
}) {
    const [searchTerm, setSearchTerm] = useState('');

    // Filter out what's already selected for the left side
    const availableItems = useMemo(() => {
        const selectedValues = new Set(selectedIds.map(String));
        return options.filter(opt => !selectedValues.has(String(opt.value)));
    }, [options, selectedIds]);

    // Handle search on the left side
    const filteredLeftItems = useMemo(() => {
        if (onSearchEnter) {
            return availableItems;
        }

        const lowerSearch = searchTerm.toLowerCase();

        return availableItems.filter(item =>
            (item?.label || '').toLowerCase().includes(lowerSearch)
        );
    }, [availableItems, searchTerm]);

    // Map the selected IDs back to full option objects for the right side
    const rightItems = useMemo(() => {
        return selectedIds
            .map(id => options.find(opt => String(opt.value) === String(id)))
            .filter(Boolean)
            .reverse();
    }, [options, selectedIds]);

    const moveAllLeft = () => {
        onChange([]);
    };

    const moveAllRight = () => {
        if (selectedIds.length + filteredLeftItems.length > MAX_LIMIT) {
            return;
        } // API Guardrail
        const newIds = filteredLeftItems.map(item => String(item.value));
        onChange([...selectedIds, ...newIds]);
    };

    const moveLeft = item => {
        onChange(selectedIds.filter(id => String(id) !== String(item.value)));
    };

    const moveRight = item => {
        if (selectedIds.length >= MAX_LIMIT) {
            return;
        }

        onChange([...selectedIds, item.value]);
    };

    const isMaxReached = selectedIds.length >= MAX_LIMIT;
    const isListTooLarge = filteredLeftItems.length > MAX_LIMIT;
    const isExceedingLimit = selectedIds.length + filteredLeftItems.length > MAX_LIMIT;

    const canSelectAll = filteredLeftItems.length > 0 && !isListTooLarge && !isExceedingLimit;

    let selectAllMessage = `${i18next.t('ui.select_all')} (${filteredLeftItems.length})`;
    if (isListTooLarge) {
        selectAllMessage = i18next.t('ui.dual_list_box.too_many_choices');
    } else if (isExceedingLimit) {
        selectAllMessage = i18next.t('ui.dual_list_box.exceeds_limit', { max: MAX_LIMIT });
    }

    return (
        <div className="tw-w-full">
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-items-center tw-w-full">
                {/* Left Column (Source) */}
                <Card className="tw-flex-1 tw-w-full tw-flex tw-flex-col" style={{ height }}>
                    <CardContent className="tw-flex-1 tw-flex tw-flex-col tw-p-4 tw-overflow-hidden">
                        <CardHeader className="tw-p-0 tw-mb-2" title={title}/>
                        <div className="tw-flex-none tw-pb-2">
                            <SearchInput
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && onSearchEnter) {
                                        e.preventDefault();
                                        // Pass the LOCAL searchTerm back to the parent
                                        onSearchEnter(searchTerm);
                                    }
                                }}
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                            />
                        </div>
                        <ScrollArea className="tw-flex-1 tw-w-full tw-pr-4">
                            {filteredLeftItems.length > 0 && (
                                <div
                                    className={`tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-2 tw-rounded-md tw-border ${
                                        !canSelectAll
                                            ? 'tw-bg-muted/50 tw-border-muted tw-cursor-not-allowed tw-text-muted-foreground'
                                            : 'tw-bg-primary/5 tw-border-primary/20 hover:tw-bg-primary hover:tw-text-primary-foreground tw-cursor-pointer tw-transition-colors'
                                    }`}
                                    onClick={canSelectAll ? moveAllRight : undefined}
                                >
                                    <span className={`tw-font-medium ${!canSelectAll ? 'tw-text-xs tw-italic' : 'tw-text-sm'}`}>
                                        {selectAllMessage}
                                    </span>
                                    <ChevronRight className={`tw-h-4 tw-w-4 ${!canSelectAll ? 'tw-opacity-50' : ''}`}/>
                                </div>
                            )}

                            {filteredLeftItems.map(item => (
                                <div
                                    className={`tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-1 tw-rounded-md tw-group ${
                                        isMaxReached
                                            ? 'tw-opacity-50 tw-cursor-not-allowed'
                                            : 'hover:tw-bg-accent tw-cursor-pointer'
                                    }`}
                                    key={item.value}
                                    onClick={isMaxReached ? undefined : () => moveRight(item)}
                                >
                                    <span className="tw-text-sm">{item.label}</span>
                                    {!isMaxReached && (
                                        <ChevronRight className="tw-h-4 tw-w-4 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"/>
                                    )}
                                </div>
                            ))}
                        </ScrollArea>
                    </CardContent>
                </Card>

                <div className="tw-hidden md:tw-flex tw-flex-none tw-flex-col tw-gap-2">
                    <ChevronRight className="tw-text-muted-foreground"/>
                    <ChevronLeft className="tw-text-muted-foreground"/>
                </div>

                {/* Right Column (Selected) */}
                <Card className="tw-flex-1 tw-w-full tw-flex tw-flex-col tw-border-primary/20 tw-bg-primary/5" style={{ height }}>
                    <CardContent className="tw-flex-1 tw-flex tw-flex-col tw-p-4 tw-overflow-hidden">
                        <CardHeader
                            className="tw-p-0 tw-mb-2"
                            title={`${i18next.t('glossary.selected').capitalize()} (${rightItems.length} / ${MAX_LIMIT})`}
                        />
                        <ScrollArea className="tw-flex-1 tw-w-full tw-pr-4">
                            {rightItems.length === 0 ? (
                                <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-xs tw-text-muted-foreground tw-italic">
                                    {i18next.t('alarms.no_items_selected').capitalize()}
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-2 tw-rounded-md tw-bg-destructive/5 tw-border tw-border-destructive/20 hover:tw-bg-destructive hover:tw-text-destructive-foreground tw-cursor-pointer tw-transition-colors"
                                        onClick={moveAllLeft}
                                    >
                                        <ChevronLeft className="tw-h-4 tw-w-4"/>
                                        <span className="tw-text-sm tw-font-medium">{i18next.t('ui.clear_selection', 'Remove All')}</span>
                                    </div>
                                    { rightItems.map(item => (
                                        <div
                                            className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-1 tw-rounded-md hover:tw-bg-destructive/10 hover:tw-text-destructive tw-group tw-cursor-pointer"
                                            key={item.value}
                                            onClick={() => moveLeft(item)}
                                        >
                                            <ChevronLeft className="tw-h-4 tw-w-4 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"/>
                                            <span className="tw-text-sm">{item.label}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
            {error && (
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-destructive">
                    <span className="tw-text-xs tw-font-medium">
                        {typeof error === 'string' ? error : error.message}
                    </span>
                </div>
            )}
        </div>
    );
}
